import { Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokenService } from '../../../services/tokenService';
import {
  consumeAuthRedirectTarget,
  isOAuthAuthorizeRedirectTarget,
  resolveAuthenticatedRedirectTarget,
} from '../authRedirect';
import AuthLoadingScreen from '../components/AuthLoadingScreen';
import { clearStytchCookies } from '../providers/clearStytchCookies';
import { useStytch } from '../providers/StytchContext';
import { resolveSessionDurationMinutes } from '../sessionPolicy';
import { type RoleAwareUser } from '../userRoles';
import { selectOrganization } from '../utils/selectOrganization';

function readStoredUser(): RoleAwareUser {
  try {
    const raw = sessionStorage.getItem('platform_user') || localStorage.getItem('platform_user');
    if (!raw) {
      return undefined;
    }
    return JSON.parse(raw) as RoleAwareUser;
  } catch {
    return undefined;
  }
}

/**
 * Handles the OAuth discovery callback from Stytch.
 *
 * After Google OAuth completes, Stytch redirects here with a `token` param.
 * We authenticate the token to get discovered organizations, then:
 * 1. If user belongs to 1+ orgs: auto-select the first org (skip org selection UI)
 * 2. If user belongs to 0 orgs: fail closed and require admin pre-approval
 *
 * This bypasses Stytch's built-in "Select an organization" screen.
 *
 * IMPORTANT: Stytch Dashboard Configuration Required
 * --------------------------------------------------
 * The redirect URL for this callback must be registered in the Stytch dashboard:
 * https://stytch.com/dashboard/redirect-urls
 *
 * Add the following URLs for each environment:
 * - Local dev:  http://localhost:5173/auth/discovery-callback
 * - Staging:    https://<staging-domain>/auth/discovery-callback
 * - Production: https://<production-domain>/auth/discovery-callback
 *
 * Ensure the URL type allows "Discovery" OAuth redirects.
 * Without this configuration, Stytch will reject the OAuth flow with error:
 * "no_match_for_provided_oauth_url"
 */
export default function DiscoveryCallback() {
  const { stytch, ready: stytchReady } = useStytch();
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasInitiated = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Wait for Stytch to be ready
      if (!stytchReady || !stytch) {
        return; // Will retry when stytchReady changes
      }
      // Guard against running the callback twice (React StrictMode, rapid dependency changes)
      if (hasInitiated.current) {
        return;
      }
      hasInitiated.current = true;
      const savedRedirectUrl = consumeAuthRedirectTarget();

      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const tokenType = params.get('stytch_token_type');

      if (!token) {
        setError('Missing token in callback');
        return;
      }

      // Only handle discovery_oauth tokens
      if (tokenType !== 'discovery_oauth') {
        setError(`Unsupported token type: ${tokenType}`);
        return;
      }

      try {
        // Clear any stale Stytch session cookies before authenticating.
        // Stale cookies from expired or deleted org sessions cause the SDK to
        // send conflicting credentials, resulting in "unauthorized_credentials".
        await clearStytchCookies();

        // Authenticate the OAuth discovery token
        const authResp = await stytch.oauth.discovery.authenticate({
          discovery_oauth_token: token,
        });

        const { intermediate_session_token, discovered_organizations } = authResp;
        const discoveredEmail =
          typeof (authResp as { email_address?: unknown }).email_address === 'string'
            ? ((authResp as { email_address: string }).email_address || '').trim().toLowerCase()
            : '';

        if (!intermediate_session_token) {
          setError('No intermediate session token received');
          return;
        }

        // Check if user belongs to any organizations
        if (discovered_organizations && discovered_organizations.length > 0) {
          const selectedOrg = selectOrganization(discovered_organizations, discoveredEmail);
          if (!selectedOrg) {
            setError(
              'Multiple organizations found for this account. Please contact support to complete sign in for the correct organization.'
            );
            return;
          }

          // SDK typings currently omit intermediate_session_token, but the API requires it.
          const exchangeOptions: any = {
            intermediate_session_token,
            organization_id: selectedOrg.organization.organization_id,
            session_duration_minutes: resolveSessionDurationMinutes(selectedOrg.organization),
          };
          const exchangeResp =
            await stytch.discovery.intermediateSessions.exchange(exchangeOptions);

          // Defense-in-depth: if the saved redirect target is /oauth/authorize,
          // skip the Moments platform token exchange. ConnectedAppAuthorize
          // handles its own login and should never set this target, but guard
          // against any future code path that might.
          if (savedRedirectUrl && isOAuthAuthorizeRedirectTarget(savedRedirectUrl)) {
            nav(savedRedirectUrl, { replace: true });
            return;
          }

          // Get the session JWT and exchange it for our platform token
          if (exchangeResp.session_jwt) {
            const token = await tokenService.exchangeStytchSession(exchangeResp.session_jwt);
            if (!token) {
              if (tokenService.consumeExchangeError() === 'partner_session_expired') {
                throw new Error('partner_session_expired');
              }
              const prompt = tokenService.consumeDrivePrompt();
              if (prompt?.authUrl) {
                // Auto-redirect to Google Drive consent (no modal)
                sessionStorage.setItem('drive_connect_pending_token', exchangeResp.session_jwt);
                if (prompt.state) {
                  sessionStorage.setItem('drive_connect_state', prompt.state);
                }
                window.location.href = prompt.authUrl;
                return;
              }
              setError('Token exchange failed');
              return;
            }
            nav(resolveAuthenticatedRedirectTarget(readStoredUser(), savedRedirectUrl), {
              replace: true,
            });
          } else {
            setError('No session JWT received after org selection');
          }
        } else {
          setError('Your account is not pre-approved yet. Contact your administrator for access.');
          return;
        }
      } catch (e: any) {
        await clearStytchCookies();
        console.error('Discovery callback error:', e);
        if (e?.message === 'partner_session_expired') {
          const message = 'Your session has expired. Please sign in again.';
          tokenService.clearTokens({ expired: true, message });
          setError(message);
          return;
        }
        setError(e?.message || 'Authentication failed');
      }
    };

    handleCallback();
  }, [stytch, stytchReady, nav]);

  if (!error) {
    return <AuthLoadingScreen testId="discovery-callback-loading-screen" />;
  }

  return (
    <AuthLoadingScreen showSpinner={false}>
      <Typography
        color="error"
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          maxWidth: 400,
          p: 3,
          textAlign: 'center',
        }}
      >
        {error}
      </Typography>
      <Typography
        sx={{
          color: '#EDF2F0',
          cursor: 'pointer',
          mt: 2,
          textDecoration: 'underline',
        }}
        onClick={() => nav('/login', { replace: true })}
      >
        Return to login
      </Typography>
    </AuthLoadingScreen>
  );
}
